import { useMemo, useState } from "react";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Badge } from "@shared/ui/badge";
import {
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import { ServiceDetailForm } from "./serviceDetailForm";
import { ContextualHelp } from "@shared/ui/contextual-help";
import {
  useAllServiceConfig,
  useSearchServiceConfig,
  useDeleteServiceConfig,
  useUpdateServiceConfig,
  useToggleServiceConfig,
} from "@features/cx/settings/hooks";
import { ServiceViewForm } from "./serviceViewForm";
import { Service } from "@features/cx/settings/types";

export function ServiceConfigurationTab() {
  // State management
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewingService, setViewingService] = useState<Service | null>(null);
  const [showViewForm, setShowViewForm] = useState(false);
  const updateConfig = useToggleServiceConfig();

  // API hooks - Using single variable name for consistency
  const {
    data: serviceConfigDetails = [],
    refetch: refetchServiceConfigDetails,
  } = useAllServiceConfig(1);

  const deleteServiceConfiguration = useDeleteServiceConfig();
  const { data: searchResults = [], refetch: refetchSearchResults } =
    useSearchServiceConfig(1, searchQuery);

  // Determine which data to display based on search query
  const displayedServices = useMemo(() => {
    return searchQuery.trim() ? searchResults : serviceConfigDetails;
  }, [searchQuery, searchResults, serviceConfigDetails]);

  // Event handlers
  const handleEdit = (service: Service) => {
    setEditingService(service);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingService(null);
    setShowForm(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleView = (service: Service) => {
    setViewingService(service);
    setShowViewForm(true);
  };

  // Badge variant helpers
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "Active":
        return "default";
      case "Inactive":
        return "secondary";
      case "Draft":
        return "outline";
      default:
        return "secondary";
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "Critical":
        return "destructive";
      case "High":
        return "default";
      case "Normal":
        return "secondary";
      case "Low":
        return "outline";
      default:
        return "secondary";
    }
  };

  const deleteServiceConfig = (service_id: any) => {
    deleteServiceConfiguration.mutate(service_id);
  };
  const handleStatusToggle = (service: any) => {
    const payload = {
      id: service.id,
      isActive: !service.isActive,
    };
    updateConfig.mutateAsync(payload);
    console.log("Toggling status for service:", service);
  };
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-start">
        <div className="flex flex-col">
          <div className="flex items-center">
            <h2 className="card-heading-text">Service Configuration</h2>
            <ContextualHelp
              title="Service Configuration"
              content="Manage individual services within your categories. Each service has specific SLA requirements, priority levels, and workflow configurations. Services must be created within existing categories and subcategories."
            />
          </div>
          <p className="card-subheading-text">
            Allows setup of specific service rules, parameters, and behaviors
            per utility service.
          </p>
        </div>

        <Button onClick={handleAddNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Service
        </Button>
      </div>

      {/* Search & Filter Section */}

      <div className="flex flex-col md:flex-row gap-4">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by service name or code..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <ContextualHelp
            title="Search Services"
            content="Search through services by name or service code. The search is case-insensitive and will match partial names or codes."
            className="absolute right-2 top-2"
          />
        </div>
      </div>

      {/* Services Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Subcategory</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Resolution Time</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    {searchQuery
                      ? `No services found matching "${searchQuery}"`
                      : "No services found. Click 'Add New Service' to create your first service."}
                  </TableCell>
                </TableRow>
              ) : (
                displayedServices.map((service) => (
                  <TableRow
                    key={service.id}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell className="font-medium">
                      {service.name}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {service.configurationCode}
                    </TableCell>
                    <TableCell>{service.supportRequestTypeDisplay}</TableCell>
                    <TableCell>
                      {service.supportRequestSubtypeDisplay}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getStatusBadgeVariant(
                          service.isActive ? "Active" : "Inactive"
                        )}
                      >
                        {service.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getPriorityBadgeVariant(
                          service.extraData?.priorityLevelDisplay
                        )}
                      >
                        {service.extraData?.priorityLevelDisplay}
                      </Badge>
                    </TableCell>
                    <TableCell>{service.extraData?.resolutionTime}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(service);
                            }}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusToggle(service);
                            }}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {service.isActive ? "Inactivate" : "Activate"}
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(service);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteServiceConfig(service.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Service Detail Form Modal */}
      {showForm && (
        <ServiceDetailForm
          service={editingService}
          open={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingService(null);
          }}
          onSave={(serviceData) => {
            setShowForm(false);
            setEditingService(null);
            refetchServiceConfigDetails();
            if (searchQuery) {
              refetchSearchResults();
            }
          }}
        />
      )}

      {showViewForm && viewingService && (
        <ServiceViewForm
          service={viewingService}
          open={showViewForm}
          onClose={() => {
            setShowViewForm(false);
            setViewingService(null);
          }}
          onEdit={() => {
            setShowViewForm(false);
            handleEdit(viewingService);
          }}
        />
      )}
    </div>
  );
}
